import { Either, fold } from "fp-ts/lib/Either";
import { makeWordPressAjaxRequest } from "./ajax";
import * as t from "io-ts";

import { Decoder } from "io-ts";
import NotificationLogger from "@/lib/notifications/notifications";
import * as n from "@/types/notifications";
import { isString } from "lodash";
import fpJsonParse from "@/util/fp-json-parse";
import { fpJsonStringify } from "@/util/fp-json-stringify";
import { pipe } from "fp-ts/function";

export type _Error = {
  msg: string;
  customNotificationToShow?: n.BreakdanceNotification;
};

export type _ErrorWithCustomNotificationToShow = _Error &
  Required<Pick<_Error, "customNotificationToShow">>;

export type CustomErrorNotificationFactory = (
  message: string
) => n.BreakdanceNotification;

export function errorHasCustomNotificationToShow(
  error: _Error
): error is _ErrorWithCustomNotificationToShow {
  return error.customNotificationToShow !== undefined;
}

export async function retrieveFromWP<I, A>(
  key: string,
  decoder: Decoder<I, A>
): Promise<Either<_Error[], A>> {
  return await makeWordPressAjaxRequest({
    action: "breakdance_retrieve_arbitrary",
    dataToPost: { key: key },
    decoder,
    processResults: r => fpJsonParse(r.data)
  });
}

export async function saveToWP<T>(
  key: string,
  data: T
): Promise<Either<_Error[], unknown>> {
  return await makeWordPressAjaxRequest({
    action: "breakdance_save_arbitrary",
    dataToPost: { key, data: fpJsonStringify(data) },
    decoder: t.unknown
  });
}

export function strToError(error: unknown): _Error {
  return {
    msg: String(error)
  };
}

export function addNotificationToError(
  error: _Error,
  customNotificationToShow: n.BreakdanceNotification
): _ErrorWithCustomNotificationToShow {
  return {
    ...error,
    customNotificationToShow
  };
}

function nonEmptyStringOrFallback(
  probablyEmptyString: string,
  fallbackString: string
): string {
  return probablyEmptyString.trim().length
    ? probablyEmptyString
    : fallbackString;
}

export function extractMessageFromUnknownErrorOrFallback(
  error: Error | string | unknown,
  fallbackMessage: string
): string {
  if (error instanceof Error) {
    return nonEmptyStringOrFallback(error.message, fallbackMessage);
  } else if (isString(error)) {
    return nonEmptyStringOrFallback(error, fallbackMessage);
  }

  return fallbackMessage;
}

export function maybeAddNotificationToError(
  err: _Error,
  customNotificationToShow?: n.BreakdanceNotification
): _Error {
  return customNotificationToShow
    ? addNotificationToError(err, customNotificationToShow)
    : err;
}

export function createErrorListFromErrorMessage(
  errorMessage: string,
  customNotificationToShow?: n.BreakdanceNotification
): _Error[] {
  return [errorMessage]
    .map(strToError)
    .map(err => maybeAddNotificationToError(err, customNotificationToShow));
}

export function createErrorListFromUnknownError(
  error: Error | string | unknown,
  fallbackMsg: string,
  notificationFactory?: CustomErrorNotificationFactory
): _Error[] {
  const errorMessage = extractMessageFromUnknownErrorOrFallback(
    error,
    fallbackMsg
  );

  return createErrorListFromErrorMessage(
    errorMessage,
    notificationFactory ? notificationFactory(errorMessage) : undefined
  );
}

export const logErrorsToUiStore = (errors: _Error[]): void => {
  errors.forEach(e => {
    if (e.customNotificationToShow) {
      NotificationLogger.log.notification(e.customNotificationToShow);
    } else {
      NotificationLogger.log.message(e.msg);
    }
  });
};

const TextValueV = t.type({
  text: t.string,
  value: t.string
});
const TextValuesV = t.array(TextValueV);
type TextValues = t.TypeOf<typeof TextValuesV>;

export async function fetchOptionData(
  action: string,
  requestData: object = {}
): Promise<TextValues> {
  return pipe(
    await makeWordPressAjaxRequest({
      action: action,
      dataToPost: { requestData },
      decoder: TextValuesV
    }),
    fold(
      (errors: _Error[]) => {
        logErrorsToUiStore(errors);

        return [];
      },
      responseItems => responseItems
    )
  );
}
